import { type HotelDetail, type HotelInventoryListItem } from '@utazzo/shared';
import { AlertTriangle, Check, Circle } from 'lucide-react';
import React, { useMemo } from 'react';

/**
 * Compact setup checklist for the editor sidebar. Tells the operator what
 * still blocks this hotel from being bookable, with a completion bar.
 */

interface HotelSetupCardProps {
  hotel: HotelDetail;
  inventory: HotelInventoryListItem[];
  inventoryLoading: boolean;
  /** Jump to the Rooms & rates tab for room/rate items. */
  onGoToRooms: () => void;
}

interface ChecklistItem {
  key: string;
  label: string;
  state: 'done' | 'todo' | 'warn';
  onClick?: () => void;
}

const fmtPrice = (amount: number, currencyCode: string | null): string => {
  const code = currencyCode ?? 'INR';
  const prefix = code === 'INR' ? '₹' : `${code} `;
  return `${prefix}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

export const HotelSetupCard = ({
  hotel,
  inventory,
  inventoryLoading,
  onGoToRooms,
}: HotelSetupCardProps): React.JSX.Element => {
  const photoCount = Array.isArray(hotel.photos) ? hotel.photos.length : 0;

  const { checklist, stats } = useMemo(() => {
    const roomsWithRates = new Set(
      inventory.map((r) => r.hotelRoomId).filter((id): id is string => Boolean(id)),
    );
    const roomsMissingRates = hotel.rooms.filter((r) => !roomsWithRates.has(r.id));

    const activePrices = inventory
      .filter((r) => r.isActive)
      .map((r) => r.adultPrice || r.fixedRoomCost || 0)
      .filter((n) => n > 0);
    const minPrice = activePrices.length ? Math.min(...activePrices) : null;

    const items: ChecklistItem[] = [
      {
        key: 'details',
        label: hotel.cityId ? 'Basic details' : 'Basic details (city missing)',
        state: hotel.name && hotel.cityId ? 'done' : 'todo',
      },
      {
        key: 'rooms',
        label:
          hotel.rooms.length > 0
            ? `${hotel.rooms.length} room${hotel.rooms.length === 1 ? '' : 's'} added`
            : 'Add rooms',
        state: hotel.rooms.length > 0 ? 'done' : 'todo',
        onClick: onGoToRooms,
      },
      {
        key: 'rates',
        label:
          hotel.rooms.length === 0
            ? 'Add rates'
            : roomsMissingRates.length === 0
              ? 'All rooms have rates'
              : `${roomsMissingRates.length} room${roomsMissingRates.length === 1 ? '' : 's'} without rates`,
        state:
          inventoryLoading || hotel.rooms.length === 0
            ? 'todo'
            : roomsMissingRates.length === 0
              ? 'done'
              : 'warn',
        onClick: onGoToRooms,
      },
      {
        key: 'photos',
        label: photoCount > 0 ? `${photoCount} photo${photoCount === 1 ? '' : 's'}` : 'Add photos',
        state: photoCount > 0 ? 'done' : 'todo',
      },
      {
        key: 'approval',
        label: hotel.isApprovedByAdmin ? 'Approved' : 'Awaiting approval',
        state: hotel.isApprovedByAdmin ? 'done' : 'todo',
      },
    ];

    return {
      checklist: items,
      stats: {
        rooms: hotel.rooms.length,
        rates: inventory.length,
        minPrice,
        currencyCode: inventory[0]?.currencyCode ?? null,
      },
    };
  }, [hotel, inventory, inventoryLoading, photoCount, onGoToRooms]);

  const doneCount = checklist.filter((c) => c.state === 'done').length;

  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <header className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
        <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Setup</h3>
        <span className="text-xs text-zinc-500 tabular-nums">
          {doneCount}/{checklist.length}
        </span>
        <span className="flex-1" />
        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-100">
          <div
            className="h-full rounded-full bg-emerald-500 transition-[width] duration-300"
            style={{ width: `${(doneCount / checklist.length) * 100}%` }}
          />
        </div>
      </header>
      <div className="space-y-0.5 px-2 py-2">
        {checklist.map((item) => {
          const icon =
            item.state === 'done' ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
            ) : item.state === 'warn' ? (
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
            ) : (
              <Circle className="h-3.5 w-3.5 shrink-0 text-zinc-300" aria-hidden />
            );
          const text = (
            <span
              className={
                'text-xs ' +
                (item.state === 'done'
                  ? 'text-zinc-500'
                  : item.state === 'warn'
                    ? 'font-medium text-amber-700'
                    : 'text-zinc-700')
              }
            >
              {item.label}
            </span>
          );
          return item.onClick && item.state !== 'done' ? (
            <button
              key={item.key}
              type="button"
              onClick={item.onClick}
              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-zinc-50"
            >
              {icon}
              {text}
            </button>
          ) : (
            <span key={item.key} className="flex items-center gap-2 px-2 py-1">
              {icon}
              {text}
            </span>
          );
        })}
      </div>
      {/* Stats footer */}
      <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-zinc-100 px-4 py-2.5 text-xs text-zinc-500">
        <span>
          <span className="font-medium text-zinc-700 tabular-nums">{stats.rooms}</span> room
          {stats.rooms === 1 ? '' : 's'}
        </span>
        <span>
          <span className="font-medium text-zinc-700 tabular-nums">{stats.rates}</span> rate
          {stats.rates === 1 ? '' : 's'}
        </span>
        {stats.minPrice !== null && (
          <span>
            from{' '}
            <span className="font-medium text-zinc-700 tabular-nums">
              {fmtPrice(stats.minPrice, stats.currencyCode)}
            </span>
          </span>
        )}
      </footer>
    </section>
  );
};
